import {Box, Skeleton} from '@mantine/core';
import {WhiteBgButton} from '@packages/ui';
import classNames from 'classnames';
import Head from 'next/head';
import {useRouter} from 'next/router';
import React from 'react';
import {TITLE_SUFFIX} from '../../../config/constants';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {
  type ResultGetCompanyInfo,
  useGetCompanyInfo,
} from '../../../core/repositories/companiesRepository';
import type {ReportType} from '../../../core/repositories/reportsRepository';
import {SpTopBottomBanner} from '../../../shared/components/Banner/SpTopBottomBanner';
import {getTopBottomBannerPosition} from '../../../shared/services/getTopBottomBannerPosition';
import {useGoogleAdBanner} from '../../../shared/services/useGoogleAdBanner';
import {useHandleStudy} from '../usecase/useHandleStudy';
import {ExperiencesHeader} from './Container/ExperiencesHeader';
import {NoContent} from './Container/Presenter/NoContent';
import {ReportCard} from './Container/Presenter/ReportCard';
import {StudyFilter} from './Container/Presenter/StudyFilter';

type ContentProps = {
  companyInfo: ResultGetCompanyInfo | undefined;
  companyId: number;
  companyName?: string;
};

const Content: React.FC<ContentProps> = ({
  companyInfo,
  companyId,
  companyName,
}) => {
  const {
    reportTypes,
    companyDepartmentMaster,
    graduationYears,
    reports,
    isLoading,
    filterItems,
    setFilterItems,
    hasNextPage,
    loadMoreReports,
  } = useHandleStudy({companyId});
  // DFPバナーのセット
  const googleAdBannerNames = [
    GoogleAdBannerName.B_RECTANGLE,
    GoogleAdBannerName.B_RECTANGLE_BOTTOM,
  ];
  useGoogleAdBanner(googleAdBannerNames);

  const handleChangeReportType = (value: string[]) => {
    setFilterItems((prev) => ({
      ...prev,
      reportType: value,
    }));
  };

  const handleChangeDepartment = (value: string[]) => {
    setFilterItems((prev) => ({
      ...prev,
      department: value.map(Number),
    }));
  };

  const handleChangeGradYear = (value: string[]) => {
    setFilterItems((prev) => ({
      ...prev,
      graduationYear: value.map(Number),
    }));
  };

  const handleChangeOrder = (value: 'recent' | 'popular') => {
    setFilterItems((prev) => ({
      ...prev,
      sortOrder: value,
    }));
  };

  return (
    <Box className="pc:w-[1200px] w-full">
      <ExperiencesHeader
        company={
          companyInfo?.ok
            ? {
                id: companyInfo.value.id,
                name: companyInfo.value.name,
                logoURL: companyInfo.value.logoURL,
                industryName: companyInfo.value.industryName,
                industryCategoryName: companyInfo.value.industryCategoryName,
                industryCategoryID: companyInfo.value.industryCategoryID,
                followerCount: companyInfo.value.followerCount,
                followed: companyInfo.value.followed,
              }
            : undefined
        }
        companyName={companyName}
      />
      <StudyFilter
        filterItems={filterItems}
        departments={
          companyDepartmentMaster?.ok ? companyDepartmentMaster.value : []
        }
        graduationYears={graduationYears}
        reportTypes={reportTypes}
        handleChangeOrder={handleChangeOrder}
        handleChangeReportType={handleChangeReportType}
        handleChangeDepartment={handleChangeDepartment}
        handleChangeGradYear={handleChangeGradYear}
      />

      <div className="mt-16 grid grid-cols-1 mx-16 gap-8 pc:mx-0 pc:w-full pc:grid-cols-3 pc:gap-16">
        {reports?.pages.map((page, pageIndex) => (
          <React.Fragment key={`page-${page.page}`}>
            {page.reports?.map((report: ReportType, reportIndex: number) => (
              <React.Fragment key={report.id}>
                <ReportCard report={report} />
                {getTopBottomBannerPosition(pageIndex * 8 + reportIndex) && (
                  <div className="w-full p-20 flex justify-center pc:hidden">
                    <SpTopBottomBanner
                      type={getTopBottomBannerPosition(
                        pageIndex * 8 + reportIndex,
                      )}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </div>
      {isLoading && (
        <div
          className={classNames(
            'mt-16 grid grid-cols-1 mx-16 gap-8 pc:mx-0 pc:w-full pc:grid-cols-3 pc:gap-16',
          )}
        >
          {Array.from({length: 9}).map((_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: order won't change
            <React.Fragment key={index}>
              <div className="flex flex-col shadow-card bg-white rounded-[20px] p-16">
                <div className="p-8 pc:p-16">
                  <Skeleton className="h-20" />
                  <Skeleton className="h-20 mt-4" />
                  <Skeleton className="h-20 mt-8" />
                </div>
                <Skeleton className="h-[68px] flex-col p-8 rounded-16" />
              </div>
              {getTopBottomBannerPosition(index * 8 + index) && (
                <div className="pc:hidden w-full p-20 flex justify-center">
                  <SpTopBottomBanner
                    type={getTopBottomBannerPosition(index * 8 + index)}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
      <Box className="w-full space-x-16">
        {reports?.pages.length === 0 && !isLoading && (
          <Box className="ml-16">
            <NoContent />
          </Box>
        )}
        {(reports === undefined || isLoading) && (
          <Skeleton height={50} className="mt-8 p-8 w-full justify-center" />
        )}
        {
          <div className="p-16 flex justify-center">
            {hasNextPage && !isLoading && (
              <WhiteBgButton
                label="もっと見る"
                isPrimary={false}
                isLarge={false}
                onClick={() => {
                  loadMoreReports();
                }}
                className="w-[80%] rounded-[20px] text-primary border-primary"
              />
            )}
          </div>
        }
      </Box>
    </Box>
  );
};

type ExperiencesPageProps = {
  companyName?: string;
};

export const ExperiencesPage: React.FC<ExperiencesPageProps> = ({
  companyName,
}) => {
  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath.replace(/\?.*$/, ''))}`;

  const {id} = router.query as {id: string};
  const companyId = Number.parseInt(id, 10);
  const {data: companyInfo} = useGetCompanyInfo({
    companyId: companyId,
  });

  return (
    <>
      <Head>
        <title key="title">
          {`${companyName}のインターン・本選考のES・選考体験記一覧${TITLE_SUFFIX}`}
        </title>
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta
          key="og:title"
          property="og:title"
          content={`${companyName}のインターン・本選考のES・選考体験記一覧`}
        />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta
          key="og:description"
          name="og:description"
          content={`${companyName}のインターン・本選考のES・選考体験記一覧です。${companyName}の本選考、インターンシップ選考で内定もしくは最終面接まで進んだ方からいただいた非常に信頼度が高い情報をまとめております。学生時代頑張ったことや志望動機などエントリーシートの書き方をはじめとした、Webテスト・適性検査の形式やGD（グループディスカッション）の問題、面接の質問と受け答え、フェルミ推定・ケース面接の心構えや解答などを掲載しております。さらに企業説明会やOB訪問、リクルーター面談で内定者が社員の方に伺った内容も多数記載しています。ぜひ、企業研究や自己分析にお役立てください。`}
        />
        <meta
          key="description"
          name="description"
          content={`${companyName}のインターン・本選考のES・選考体験記一覧です。${companyName}の本選考、インターンシップ選考で内定もしくは最終面接まで進んだ方からいただいた非常に信頼度が高い情報をまとめております。学生時代頑張ったことや志望動機などエントリーシートの書き方をはじめとした、Webテスト・適性検査の形式やGD（グループディスカッション）の問題、面接の質問と受け答え、フェルミ推定・ケース面接の心構えや解答などを掲載しております。さらに企業説明会やOB訪問、リクルーター面談で内定者が社員の方に伺った内容も多数記載しています。ぜひ、企業研究や自己分析にお役立てください。`}
        />
        <meta
          key="keywords"
          name="keywords"
          content="外資,就活,インターンシップ,新卒採用,会社説明会,コラム,ES・体験記,投資銀行,コンサル,商社"
        />
      </Head>
      <Content
        companyInfo={companyInfo}
        companyId={companyId}
        companyName={companyName}
      />
    </>
  );
};
